import { FC, useEffect, useMemo, useState } from "react"
import { useForm, SubmitHandler, Controller } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import { FormInput } from "@/shared/ui/form-input"
import notify from "@/shared/lib/notify"
import { getErrorMessage } from "@/shared/consts/error-codes"
import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import { fetchSetAcl } from "../model"
import { selectUsersGroups, selectEmployeesGroups, fetchUsersGroups, fetchEmployeesGroups } from "@/entities/groups"
import { TargetType, AclStrategy } from "@/shared/api/groups"
import { ACL_STRATEGY_OPTIONS, NULL_GROUP_OPTION_VALUE } from "@/entities/visibility-acl"
import { getUsers } from "@/shared/api/users"
import { getDisplayName } from "@/entities/user"
import * as yup from "yup"
import type { VisibilityAclFormValues } from "@/entities/visibility-acl"
import AsyncSelect from "react-select/async"
import ArrowDown from "@/shared/assets/images/ArrowDown.svg"

const TARGET_TYPE_OPTIONS = [
    { value: TargetType.GROUP, label: "Группа" },
    { value: TargetType.USER, label: "Пользователь" },
]

interface AddAclFormValues extends VisibilityAclFormValues {
    target_type: TargetType
    target_id: string
}

const loadUserOptions = (inputValue: string): Promise<{ value: string; label: string }[]> =>
    getUsers({ page: 1, limit: 50, search: inputValue || undefined, all: true })
        .then((r) =>
            r.data.items.map((u) => ({
                value: u.id,
                label: getDisplayName(u),
            }))
        )
        .catch((): { value: string; label: string }[] => [])

export const AddAclForm: FC = () => {
    const dispatch = useAppDispatch()
    const usersGroups = useAppSelector(selectUsersGroups)
    const employeesGroups = useAppSelector(selectEmployeesGroups)
    const [selectedUserOption, setSelectedUserOption] = useState<{ value: string; label: string } | null>(null)

    const allGroups = useMemo(
        () => [...usersGroups, ...employeesGroups],
        [usersGroups, employeesGroups]
    )

    const groupTargetOptions = useMemo(
        () => allGroups.map((g) => ({ value: g.id, label: g.name })),
        [allGroups]
    )

    const visibleGroupsOptions = useMemo(
        () => [
            { value: NULL_GROUP_OPTION_VALUE, label: "Все группы" },
            ...allGroups.map((g) => ({ value: g.id, label: g.name })),
        ],
        [allGroups]
    )

    const {
        handleSubmit,
        control,
        formState: { errors, isSubmitting },
        reset,
        setValue,
        watch,
    } = useForm<AddAclFormValues>({
        defaultValues: {
            target_type: TargetType.GROUP,
            target_id: "",
            acl_strategy: AclStrategy.INHERIT_ONLY,
            visible_group_ids: null,
        },
        resolver: yupResolver(
            yup.object().shape({
                target_type: yup.string().required("Выберите тип объекта"),
                target_id: yup.string().required("Выберите объект"),
                acl_strategy: yup.string().optional(),
                visible_group_ids: yup.array().of(yup.string()).nullable().optional(),
            })
        ) as any,
    })

    const targetType = watch("target_type", TargetType.GROUP)
    const strategy = watch("acl_strategy", AclStrategy.INHERIT_ONLY)

    useEffect(() => {
        dispatch(fetchUsersGroups())
        dispatch(fetchEmployeesGroups())
    }, [dispatch])

    useEffect(() => {
        setValue("target_id", "")
        setSelectedUserOption(null)
    }, [targetType, setValue])

    useEffect(() => {
        if (targetType === TargetType.GROUP && strategy === AclStrategy.INHERIT_ONLY) {
            setValue("visible_group_ids", null)
        }
    }, [targetType, strategy, setValue])

    const onSubmit: SubmitHandler<AddAclFormValues> = async (data) => {
        let visibleGroupIds: string[] | null = null
        if (data.visible_group_ids && data.visible_group_ids.length > 0) {
            visibleGroupIds = data.visible_group_ids.filter((id) => id !== NULL_GROUP_OPTION_VALUE)
            if (visibleGroupIds.length === 0) visibleGroupIds = null
        }
        if (data.visible_group_ids?.includes(NULL_GROUP_OPTION_VALUE)) {
            visibleGroupIds = null
        }

        try {
            await dispatch(
                fetchSetAcl({
                    target_type: data.target_type,
                    target_id: data.target_id,
                    ...(data.target_type === TargetType.GROUP && { acl_strategy: data.acl_strategy }),
                    visible_group_ids:
                        data.target_type === TargetType.GROUP && strategy === AclStrategy.INHERIT_ONLY
                            ? null
                            : visibleGroupIds,
                })
            ).unwrap()
            notify.success("ACL успешно создан")
            setSelectedUserOption(null)
            reset({ target_type: data.target_type, target_id: "", acl_strategy: AclStrategy.INHERIT_ONLY, visible_group_ids: null })
        } catch (error) {
            notify.error(getErrorMessage(error))
        }
    }

    const showStrategySelect = targetType === TargetType.GROUP
    const showVisibleGroupsSelect =
        targetType === TargetType.USER || strategy !== AclStrategy.INHERIT_ONLY

    return (
        <div className="box !h-full">
            <div className="box_header !mb-2 font-medium">
                Создать ACL
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
                <FormInput
                    label="Тип объекта"
                    id="target_type"
                    options={TARGET_TYPE_OPTIONS}
                    control={control as any}
                    error={errors.target_type}
                    type="select"
                    required
                    placeholder="Выберите тип"
                />
                {targetType === TargetType.GROUP ? (
                    <FormInput
                        label="Группа"
                        id="target_id"
                        options={groupTargetOptions}
                        control={control as any}
                        error={errors.target_id}
                        type="select"
                        required
                        placeholder="Выберите группу"
                    />
                ) : (
                    <div className={`flex relative flex-col gap-[6px] pb-[8px] border-b-[1.5px] border-[#B9C9C6] ${errors.target_id && "border-color-danger"}`}>
                        <div className="flex justify-between w-full items-center">
                            <label className="font-semibold uppercase text-base text-[#828282]">
                                Пользователь
                            </label>
                            {errors.target_id && (
                                <label className="text-base flex items-center text-color-danger">
                                    {errors.target_id.message}
                                </label>
                            )}
                        </div>
                        <Controller
                            control={control}
                            name="target_id"
                            rules={{ required: "Выберите пользователя" }}
                            render={({ field }) => (
                                <AsyncSelect
                                    {...field}
                                    classNames={{
                                        control: () => "w-full !outline-0 !shadow-none !min-h-[auto] !border-0 !border-none !p-0 !outline-none",
                                        valueContainer: () => "!border-0 !outline-0 !p-0 text-color-dark text-lg",
                                        placeholder: () => "text-color-dark/70 text-base !m-0 text-color-dark",
                                        menu: () => "!left-[0]",
                                        input: () => "!m-0 !p-0",
                                        menuPortal: () => "!z-[9999]",
                                    }}
                                    loadOptions={loadUserOptions}
                                    value={selectedUserOption?.value === field.value ? selectedUserOption : (field.value ? { value: field.value, label: field.value } : null)}
                                    onChange={(e) => {
                                        const option = e as { value: string; label: string } | null
                                        field.onChange(option?.value ?? null)
                                        setSelectedUserOption(option)
                                    }}
                                    placeholder="Введите имя или телефон для поиска..."
                                    noOptionsMessage={() => "Начните вводить для поиска"}
                                    menuPosition="fixed"
                                    menuPortalTarget={document.body}
                                    components={{
                                        DropdownIndicator: () => <ArrowDown height={20} width={20} />,
                                        IndicatorSeparator: () => null,
                                    }}
                                />
                            )}
                        />
                    </div>
                )}
                {showStrategySelect && (
                    <FormInput
                        label="Стратегия ACL"
                        id="acl_strategy"
                        options={ACL_STRATEGY_OPTIONS}
                        control={control as any}
                        error={errors.acl_strategy}
                        type="select"
                        required={false}
                        placeholder="Выберите стратегию"
                    />
                )}
                {showVisibleGroupsSelect && (
                    <FormInput
                        label="Видимые группы"
                        id="visible_group_ids"
                        options={visibleGroupsOptions}
                        control={control as any}
                        error={errors.visible_group_ids as any}
                        type="select"
                        isMulti
                        required={false}
                        placeholder="Выберите видимые группы"
                    />
                )}
                <button
                    className="form_handle_button"
                    disabled={isSubmitting}
                    type="submit"
                >
                    Создать
                </button>
            </form>
        </div>
    )
}
